<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card elevation="4" :loading="loading">
          <v-card-title class="text-indigo text-button">
            {{ editing ? "Modificar cliente" : "Crear cliente" }}
          </v-card-title>

          <v-form ref="formRef" @submit.prevent="save">
            <v-container fluid>
              <v-row>
                <v-col cols="12">
                  <h4 class="text-button text-primary">Datos de la aplicación</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col v-if="editing" cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          :model-value="data.id"
                          label="ID"
                          prepend-inner-icon="mdi-identifier"
                          readonly
                          hint="Identificador interno, asignado por el sistema."
                          persistent-hint
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          v-model="data.username"
                          label="Nombre de usuario"
                          prepend-inner-icon="mdi-application"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('username')"
                          hint="Debe coincidir con el client_id o el usuario de servicio registrado en el proveedor de identidad."
                          persistent-hint
                          @update:model-value="errors.clear('username')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          v-model="data.name"
                          label="Nombre"
                          prepend-inner-icon="mdi-rename-box"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('name')"
                          @update:model-value="errors.clear('name')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          v-model="data.email"
                          label="Email (opcional)"
                          prepend-inner-icon="mdi-email"
                          :disabled="saving"
                          :rules="[rules.emailOptional]"
                          :error-messages="errors.for('email')"
                          hint="Contacto responsable de la aplicación."
                          persistent-hint
                          @update:model-value="errors.clear('email')"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>

                <v-col cols="12" lg="6">
                  <h4 class="text-button text-primary">Acceso</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-skeleton-loader v-if="loading" type="button" />
                    <v-switch
                      v-else
                      v-model="data.state"
                      color="success"
                      inset
                      true-value="ACTIVE"
                      false-value="INACTIVE"
                      :disabled="saving"
                      :label="data.state === 'ACTIVE' ? 'Activado' : 'Desactivado'"
                      hint="Un cliente desactivado no podrá consumir la API ni firmar."
                      persistent-hint
                    />
                  </v-container>
                </v-col>

                <v-col cols="12" lg="6">
                  <h4 class="text-button text-primary">Privilegios</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-skeleton-loader v-if="loading" type="list-item-three-line" />
                    <v-select
                      v-else
                      v-model="data.roles"
                      :items="roleItems"
                      :item-props="true"
                      label="Roles (opcional)"
                      multiple
                      chips
                      clearable
                      :disabled="saving"
                      :error-messages="errors.for('roles')"
                      hint="El rol Admin ya incluye todos los privilegios, así que se selecciona solo."
                      persistent-hint
                    />
                  </v-container>
                </v-col>
              </v-row>
            </v-container>

            <v-card-actions>
              <v-spacer />
              <v-btn
                type="submit"
                variant="text"
                color="success"
                class="mx-1"
                :loading="saving"
                :disabled="loading"
              >
                <v-icon class="mr-1">mdi-content-save</v-icon>Guardar
              </v-btn>
              <v-btn
                variant="text"
                color="error"
                class="mx-1"
                :disabled="saving"
                @click="router.push('/admin/clients')"
              >
                Cancelar
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { UserRoles } from "bancojs";
import type { ClientCreatePayload, UserRole } from "bancojs";
import { banco } from "@/api/client";
import { useApiErrors } from "@/composables/useApiErrors";
import { useFormErrors } from "@/composables/useFormErrors";
import { useNotificationsStore } from "@/stores/notifications";
import { roleLabel } from "@/utils/labels";

const route = useRoute();
const router = useRouter();
const { report } = useApiErrors();
const notifications = useNotificationsStore();
const errors = useFormErrors();

const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);
const editing = computed(() => !!route.params.id);
const clientId = computed(() => Number(route.params.id));

const loading = ref(true);
const saving = ref(false);
const data = ref({
  id: undefined as number | undefined,
  username: "",
  name: "",
  email: "",
  state: "ACTIVE",
  roles: [] as UserRole[],
});

const rules = {
  required: (val: unknown) => !!val || "Campo requerido.",
  // El email es opcional para clientes: ClientDTO no lo marca @NotBlank.
  emailOptional: (val: unknown) =>
    !val || /.+@.+/.test(String(val)) || "Email inválido.",
};

const roleItems = computed(() =>
  UserRoles.map((role) => ({
    title: roleLabel(role),
    value: role,
    props: { disabled: role !== "ADMIN" && data.value.roles.includes("ADMIN") },
  }))
);

watch(
  () => data.value.roles,
  (roles) => {
    if (roles.length > 1 && roles.includes("ADMIN")) data.value.roles = ["ADMIN"];
  }
);

async function load() {
  if (!editing.value) {
    loading.value = false;
    return;
  }
  try {
    Object.assign(data.value, await banco.admin.clients.get(clientId.value));
  } catch (err) {
    report(err, {
      title: "Cliente",
      fallback: "Ocurrió un error al recuperar la información del cliente.",
    });
  } finally {
    loading.value = false;
  }
}

async function save() {
  const validation = await formRef.value?.validate();
  if (validation && !validation.valid) return;

  errors.reset();
  saving.value = true;

  const payload: ClientCreatePayload = {
    username: data.value.username,
    name: data.value.name,
    email: data.value.email || undefined,
    state: data.value.state as "ACTIVE" | "INACTIVE",
    roles: data.value.roles,
  };

  try {
    if (editing.value) {
      await banco.admin.clients.update(clientId.value, payload);
      notifications.success("Se guardaron los cambios en el cliente.", "Guardado");
    } else {
      await banco.admin.clients.create(payload);
      notifications.success("Se creó el cliente exitosamente.", "Creado");
    }
    router.push("/admin/clients");
  } catch (err) {
    report(err, {
      title: "Cliente",
      fallback: "Ocurrió un error al guardar el cliente.",
      form: errors,
    });
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>
